import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/brand";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, CalendarDays, ChevronDown } from "lucide-react";
import { fmtDate } from "@/lib/format";

const YEARS = Array.from({ length: 16 }, (_, i) => String(new Date().getFullYear() + i));
const MONTHS = ["01","02","03","04","05","06","07","08","09","10","11","12"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function MakeCallForm({ counterOf = null, defaultText = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const idemKey = useRef(crypto.randomUUID());
  const started = useRef(false);

  const [prediction, setPrediction] = useState(defaultText);
  const [deadlineType, setDeadlineType] = useState("none");
  const [year, setYear] = useState(YEARS[3]);
  const [month, setMonth] = useState("12");
  const [exactDate, setExactDate] = useState(null);
  const [showConf, setShowConf] = useState(false);
  const [confidence, setConfidence] = useState(75);
  const [category, setCategory] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [sealedDate, setSealedDate] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [hp, setHp] = useState(""); // honeypot — hidden from humans
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const onType = (v) => {
    setPrediction(v);
    if (!started.current && v.length > 0) { started.current = true; track("call_started", { counter: !!counterOf }); }
  };

  const deadlineValue = () => {
    if (deadlineType === "year") return year;
    if (deadlineType === "month_year") return `${year}-${month}`;
    if (deadlineType === "exact") return exactDate ? exactDate.toISOString().slice(0, 10) : null;
    return null;
  };

  const canLock = prediction.trim().length >= 3 && (user || guestName.trim().length >= 1) &&
    (visibility !== "sealed" || sealedDate);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        prediction_text: prediction.trim(),
        deadline_type: deadlineType,
        deadline_value: deadlineValue(),
        confidence: showConf ? confidence : null,
        category: category || null,
        visibility,
        guest_name: user ? null : guestName.trim(),
        idempotency_key: idemKey.current,
        counter_of: counterOf,
        hp,
        sealed_reveal_at: visibility === "sealed" && sealedDate ? sealedDate.toISOString() : null,
      };
      const { data } = await api.post("/calls", payload);
      if (data.claim_token) sessionStorage.setItem(`claim_${data.short_id}`, data.claim_token);
      setConfirmOpen(false);
      navigate(`/c/${data.short_id}`, { state: { justCreated: true, claim_token: data.claim_token } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not lock your Call");
      setBusy(false);
    }
  };

  return (
    <div data-testid="make-call-form" className="card-brutal card-brutal-lg p-5 sm:p-7 bg-white">
      {/* Honeypot: hidden from real users; bots that fill it are rejected server-side */}
      <input
        type="text" tabIndex={-1} autoComplete="off" aria-hidden="true"
        value={hp} onChange={(e) => setHp(e.target.value)}
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <label className="font-display text-lg font-bold flex items-center gap-2 mb-2">I predict…</label>
      <Textarea
        data-testid="prediction-input"
        value={prediction}
        onChange={(e) => onType(e.target.value)}
        placeholder="Apple will release a foldable iPhone before 2028."
        maxLength={500}
        rows={3}
        className="border-2 border-ink text-lg sm:text-xl font-display font-medium resize-none focus-visible:ring-0"
      />
      <div className="text-right text-xs text-muted-foreground font-mono mt-1">{prediction.length}/500</div>

      <div className="grid sm:grid-cols-2 gap-4 mt-3">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide">By when?</label>
          <Select value={deadlineType} onValueChange={setDeadlineType}>
            <SelectTrigger data-testid="deadline-type" className="border-2 border-ink mt-1 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No specific deadline</SelectItem>
              <SelectItem value="year">A year</SelectItem>
              <SelectItem value="month_year">A month & year</SelectItem>
              <SelectItem value="exact">An exact date</SelectItem>
            </SelectContent>
          </Select>
          {deadlineType === "year" && (
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="deadline-year" className="border-2 border-ink mt-2 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {deadlineType === "month_year" && (
            <div className="flex gap-2 mt-2">
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="border-2 border-ink h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={m}>{MONTH_LABELS[i]}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="border-2 border-ink h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {deadlineType === "exact" && (
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="deadline-exact" className="w-full mt-2 h-11 border-2 border-ink bg-white flex items-center justify-between px-3 btn-press shadow-brutal-sm">
                  <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4" />{exactDate ? fmtDate(exactDate.toISOString()) : "Pick a date"}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border-2 border-ink"><Calendar mode="single" selected={exactDate} onSelect={setExactDate} /></PopoverContent>
            </Popover>
          )}
        </div>

        <div>
          <label className="text-xs font-bold uppercase tracking-wide">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="category-select" className="border-2 border-ink mt-1 h-11"><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <button data-testid="toggle-confidence" onClick={() => setShowConf(!showConf)}
          className="text-xs font-bold uppercase tracking-wide flex items-center gap-1">
          Confidence {showConf ? "" : "(optional)"} <ChevronDown className={`w-3 h-3 transition-transform ${showConf ? "rotate-180" : ""}`} />
        </button>
        {showConf && (
          <div className="flex items-center gap-4 mt-2">
            <Slider data-testid="confidence-slider" value={[confidence]} min={50} max={100} step={1}
              onValueChange={(v) => setConfidence(v[0])} className="flex-1" />
            <span className="font-mono font-bold text-lg w-14 text-right">{confidence}%</span>
          </div>
        )}
      </div>

      <div className="mt-4">
        <label className="text-xs font-bold uppercase tracking-wide">Visibility</label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {[["public", "Public"], ["unlisted", "Unlisted"], ["sealed", "🔐 Sealed"]].map(([v, label]) => (
            <button key={v} data-testid={`visibility-${v}`} onClick={() => setVisibility(v)}
              className={`h-10 border-2 border-ink text-sm font-bold btn-press shadow-brutal-sm ${visibility === v ? "bg-brand text-white" : "bg-white"}`}>
              {label}
            </button>
          ))}
        </div>
        {visibility === "sealed" && (
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="sealed-date" className="w-full mt-2 h-11 border-2 border-ink bg-white flex items-center gap-2 px-3 btn-press shadow-brutal-sm">
                <CalendarDays className="w-4 h-4" /> {sealedDate ? `Reveals ${fmtDate(sealedDate.toISOString())}` : "Choose a reveal date"}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 border-2 border-ink"><Calendar mode="single" selected={sealedDate} onSelect={setSealedDate} /></PopoverContent>
          </Popover>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          {visibility === "public" && "Visible immediately in public feeds."}
          {visibility === "unlisted" && "Only people with the link can see it."}
          {visibility === "sealed" && "The prediction stays hidden until the reveal date, but the lock time is public."}
        </p>
      </div>

      {!user && (
        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-wide">Your name</label>
          <Input data-testid="guest-name-input" value={guestName} onChange={(e) => setGuestName(e.target.value)}
            placeholder="e.g. Ben" className="border-2 border-ink mt-1 h-11" />
          <p className="text-xs text-muted-foreground mt-1">You'll be shown as <b>{guestName || "Ben"} · Guest</b>. No account needed.</p>
        </div>
      )}

      <button data-testid="lock-it-in-btn" disabled={!canLock} onClick={() => setConfirmOpen(true)}
        className="w-full mt-6 h-14 border-2 border-ink bg-ink text-white font-display font-bold text-xl tracking-wide btn-press shadow-brutal flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
        <Lock className="w-5 h-5" strokeWidth={3} /> LOCK IT IN
      </button>
      <p className="text-center text-xs text-muted-foreground mt-2">Once it's locked, the prediction itself can't be changed.</p>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="card-brutal border-2 border-ink sm:max-w-md" data-testid="confirm-lock-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-bold">Lock this in?</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">This becomes permanent and cannot be edited:</DialogDescription>
          <div className="border-2 border-ink bg-secondary p-4">
            <p className="font-display text-lg font-semibold">“{prediction.trim()}”</p>
            <p className="text-xs font-mono mt-2 text-muted-foreground">
              {user ? `@${user.username}` : `${guestName} · Guest`}
              {showConf && ` · ${confidence}% confident`}
            </p>
          </div>
          <button data-testid="confirm-lock-btn" disabled={busy} onClick={submit}
            className="w-full h-12 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm">
            {busy ? "Locking…" : "🔒 Yes, put it on the record"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
